// import { format } from "date-fns";

// Timezone-safe date formatting utilities
// These functions ensure dates are formatted consistently regardless of user's timezone

/**
* Format a date consistently by using UTC date components to avoid timezone shifts
* @param {Date|Timestamp} date - The date to format (can be JS Date or Firestore Timestamp)
* @param {string} formatString - The format string (default: "MM/dd/yyyy")
* @returns {string} - Formatted date string using UTC components
*/
export function formatDateUTC(date, formatString = "MM/dd/yyyy") {
if (!date) return "";
// Handle Firestore Timestamp
if (date.toDate && typeof date.toDate === 'function') {
date = date.toDate();
}
// Handle Date object or timestamp
if (!(date instanceof Date)) {
date = new Date(date);
}
// Create a new date using UTC components to avoid timezone shifts
const utcDate = new Date(
date.getUTCFullYear(),
date.getUTCMonth(),
date.getUTCDate(),
date.getUTCHours(),
date.getUTCMinutes(),
date.getUTCSeconds(),
date.getUTCMilliseconds()
);
// Format using the UTC-based date
// console.log("    getUTCDate: ", date.getUTCDate(), "  date.getDate(): ", date.getDate());
// console.log("    getUTCDate: ", utcDate.getUTCDate(), "  utcDate.getDate(): ", utcDate.getDate());

// return (date.getMonth() + 1) + "/" + date.getDate() + "/" + date.getFullYear();
return (utcDate.getMonth() + 1) + "/" + utcDate.getDate() + "/" + utcDate.getFullYear();

// return format(utcDate, formatString);
}

/**
* Format a date for form fields - ensures consistent display regardless of timezone
* @param {Date|Timestamp} date - The date to format
* @returns {string} - Formatted date string (M/d/yyyy format)
*/
export function formatFormFieldDate(date) {
return formatDateUTC(date, "M/d/yyyy");
}

/**
* Format a date for annotations - ensures consistent display regardless of timezone
* @param {Date|Timestamp} date - The date to format
* @returns {string} - Formatted date string (MM/dd/yyyy format)
*/
export function formatAnnotationDate(date) {
return formatDateUTC(date, "MM/dd/yyyy");
}

/**
* Get current date formatted consistently in UTC
* @param {string} formatString - The format string (default: "MM/dd/yyyy")
* @returns {string} - Current date formatted in UTC
*/
export function getCurrentDateUTC(formatString = "MM/dd/yyyy") {
return formatDateUTC(new Date(), formatString);
}

/**
* Sanity range for document date form fields - real estate document dates
* are never more than roughly a century in the past or future. Guards
* against react-datepicker firing onChange with a malformed, syntactically-
* valid-but-absurd Date while the user is still mid-typing a year (e.g. a
* stray extra digit turning "2024" into "88742"), which otherwise reaches
* Firestore's Timestamp.fromDate() and throws "Timestamp seconds out of
* range" - the field then permanently fails to save (Sentry-reported crash
* on the party document-fill page).
* @param {Date} date
* @returns {boolean}
*/
export function isPlausibleFormFieldDate(date) {
if (!(date instanceof Date) || isNaN(date.getTime())) return false;
const year = date.getFullYear();
return year >= 1900 && year <= 2100;
}

/**
* Parse a date string and create a Date object at noon local time to avoid timezone shifts
* This ensures that date-only values (like "10/14/2023") display as the correct date
* regardless of the user's timezone
* @param {string} dateString - The date string to parse (e.g., "10/14/2023" or "May 27, 2019")
* @returns {Date|null} - Date object set to noon local time, or null if invalid
*/
export function parseDateSafe(dateString) {
if (!dateString) return null;

// Parse the date string
const parsedDate = new Date(dateString);
if (isNaN(parsedDate.getTime())) {
return null;
}

// Create a new date at noon local time using the parsed date's components
// This prevents timezone shifts when displaying dates without times
const safeDate = new Date(
parsedDate.getFullYear(),
parsedDate.getMonth(),
parsedDate.getDate(),
12, // Set to noon to avoid any daylight saving or timezone boundary issues
0,
0,
0
);

return safeDate;
}